export const GOLDEN_RATIO = (1 + Math.sqrt(5)) / 2
export const EPSILON = 0.00001

/*
 * @doctests
 *
 * ```js
 * t.is(sum([1,2,3]), 6)
 * t.is(sum([-5, 8, 0]), 3)
 * t.is(sum([]), 0)
 * ```
 */
export function sum(nums) {
  return nums.reduce((sum, num) => sum + num, 0)
}

/*
 * @doctests
 *
 * ```js
 * t.is(avg(0), 0)
 * t.is(avg(1, 2, 3), 2)
 * t.is(avg(10, 20), 15)
 * ```
 */
export function avg(...nums) {
  return nums.reduce((sum, num) => sum + num, 0) / nums.length
}

/*
 * @doctests
 *
 * ```js
 * t.is(magnitude(3, 4), 5)
 * t.is(magnitude(1, 1, 1), Math.sqrt(3))
 * ```
 */
export function magnitude(...nums) {
  return Math.sqrt(nums.reduce((mag, num) => mag + num * num, 0))
}

/*
 * @doctests
 *
 * ```js
 * t.deepEqual([0,1,2,3,4,5,6].map(fan), [-0, 1, -1, 2, -2, 3, -3 ])
 * ```
 * fan expects natural number inputs and will monotonically oscillate (around x-axis)
 */
export function fan(k) {
  return Math.ceil(0.5 * k) * Math.pow(-1, k + 1)
}

/*
 * @doctests
 *
 * ```js
 * t.deepEqual([0,1,2,3,4,5,6].map((x, _, xs) => fan2(x, xs.length)), [-3, -2, -1, 0, 1, 2, 3])
 * t.is(fan2(0, 1), 0)
 * ```
 * fan2 expects natural number inputs and will linearly represents values around x-axis
 */
export function fan2(k, total) {
  return k - 0.5 * (total - 1)
}

export function unitVector({ x1, x2, y1, y2 }) {
  const dx = x2 - x1
  const dy = y2 - y1
  const mag = magnitude(dx, dy)
  return {
    vx: dx / mag,
    vy: dy / mag
  }
}

export function orthoVector({ x1, x2, y1, y2 }) {
  const { vx, vy } = unitVector({ x1, x2, y1, y2 })
  return {
    vx: vy,
    vy: -vx
  }
}

/*
 * @doctests
 *
 * ```js
 * t.is(isNumber(0), true)
 * t.is(isNumber(1), true)
 * t.is(isNumber("2"), true)
 * t.is(isNumber(3e4), true)
 * t.is(isNumber([5]), true)
 * t.is(isNumber({}), false)
 * t.is(isNumber(NaN), false)
 * t.is(isNumber("cat"), false)
 * ```
 */
export function isNumber(value) {
  return !isNaN(value - parseFloat(value))
}

export function rand(low, high) {
  return (high - low) * Math.random() + low
}
